import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Container,
  Collapse,
  Fab,
} from '@mui/material';
import {
  Add as AddIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import ShapeBackground from '../common/ShapeBackground';
import DisbursementRuleForm from './DisbursementRuleForm';
import { useListDisbursementRules } from '../../hooks/useListDisbursementRules';
import { DisbursementRule } from '../../types';

const InfoBlurb: React.FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
    <InfoIcon color="primary" sx={{ fontSize: 56, mb: 2 }} />
    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
      Automatic Disbursements
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, textAlign: 'center' }}>
      Disbursement rules let you automatically transfer a percentage of your spending in specific categories to your savings or investment accounts. Click the + button to create your first rule!
    </Typography>
  </Box>
);

const DisbursementsPage: React.FC = () => {
  const [formOpen, setFormOpen] = useState(false);
  const { data: rules, isLoading, isError, refetch } = useListDisbursementRules();

  // Optionally, handle deletion with a mutation and refetch
  // const handleDeleteRule = (ruleId: number) => { ... }

  return (
    <Box sx={{ 
      position: 'relative',
      minHeight: '100vh',
      display: 'flex',
      pt: 8,
    }}>
      <ShapeBackground />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, minHeight: 500, position: 'relative' }}>
          <InfoBlurb />
          {isLoading ? (
            <Typography>Loading disbursement rules...</Typography>
          ) : isError ? (
            <Typography color="error">Failed to load disbursement rules.</Typography>
          ) : (
            <>
              <Collapse in={formOpen} timeout="auto" unmountOnExit sx={{ mb: 4 }}>
                <DisbursementRuleForm
                  onSubmit={() => { setFormOpen(false); refetch(); }}
                  onCancel={() => setFormOpen(false)}
                />
              </Collapse>
              {(!rules || rules.length === 0) ? (
                <Typography sx={{ mt: 2 }}>No disbursement rules yet. Click the + button to create your first rule!</Typography>
              ) : (
                <Grid container spacing={2}>
                  {Array.isArray(rules) && rules.map((rule: DisbursementRule) => (
                    <Grid item xs={12} key={rule.id}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="h6">
                                {rule.categoryDetails?.primaryDisplay} - {rule.categoryDetails?.detailedDisplay}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {rule.categoryDetails?.description}
                              </Typography>
                              <Box sx={{ mt: 2 }}>
                                {Array.isArray(rule.destinationAccounts) && rule.destinationAccounts.map((account) => (
                                  <Chip
                                    key={account.id}
                                    label={`${account.name} (${(rule.percentage * 100).toFixed(0)}%)`}
                                    sx={{ mr: 1, mb: 1 }}
                                  />
                                ))}
                              </Box>
                            </Box>
                            {/* TODO: Add delete functionality */}
                            {/* <IconButton onClick={() => handleDeleteRule(rule.id)} color="error">
                              <DeleteIcon />
                            </IconButton> */}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
          <Fab
            color="primary"
            size="large"
            onClick={() => setFormOpen(true)}
            disabled={formOpen}
            aria-label="Add Rule"
            sx={{ position: 'absolute', bottom: 32, right: 32, zIndex: 1300 }}
          >
            <AddIcon sx={{ fontSize: 36 }} />
          </Fab>
        </Paper>
      </Container>
    </Box>
  );
};

export default DisbursementsPage; 